import React, { useCallback, useEffect, useMemo, type ReactElement } from 'react';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { SECTION_ITEM_PLANS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_PLANS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { SkeletonNavMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { useExpandableEntryPointTrigger } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-expandable-entry-point-trigger/index.tsx';
import { useCurrentUser } from '@jira/platform__services-user-current/src/main.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import { functionUnionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs';

import { PACKAGE_NAME, TEAM_NAME } from '../../constants.tsx';
import { Nav4PlansFooter } from '../common/footer/Nav4PlansFooter.tsx';
import { asyncPlansSectionPlanListViewQuery } from './async-plans-list/asyncPlansSectionPlanListViewQuery.entrypoint.tsx';

const useCurrentUserAndActivationId = functionUnionWithCondition(
	() => expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false),
	() => {
		const {
			data: {
				user: { accountId: accountIdFromUser },
			},
		} = useCurrentUser();
		const activationId = useActivationId();
		return { accountId: accountIdFromUser || undefined, activationId };
	},
	() => ({ accountId: undefined, activationId: undefined }),
);

type Props = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
};

export const Nav4PlansDefault: React.FC<Props> = componentWithFG(
	'navx-global-side-nav',
	Nav4PlansDefaultGSN,
	Nav4PlansDefaultNav4,
);

function Nav4PlansDefaultGSN(props: Props) {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		hasDragIndicator,
		isDragging,
		isExpanded,
		onExpandFlyoutToggle,
		visualContentRef,
	} = props;
	const cloudId = useCloudId();

	const { accountId, activationId } = useCurrentUserAndActivationId();
	const entryPointParams = useMemo(
		() => ({ cloudId, accountId, activationId }),
		[cloudId, accountId, activationId],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncPlansSectionPlanListViewQuery,
		entryPointParams,
	);
	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PLANS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();
	const menuId = MENU_ID_PLANS;

	// remove the entire useEffect when cleaning up nav4-show-plans-when-hidden-by-default
	// we should be using the route based entry point approach instead
	// https://hello.atlassian.net/wiki/spaces/UAF/pages/2754170095/Tutorial+EntryPoints+in+Routing
	// If starting life expanded, make sure to load.
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isExpanded) {
			entryPointActions.load();
		}
	}, [isExpanded, entryPointActions]);

	const handleSystemToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			}
		},
		[entryPointActions],
	);

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.PLANS,
					sectionItem: SECTION_ITEM_PLANS,
				});
			}
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[entryPointActions, onExpandFlyoutToggle, sendSectionItemAnalyticsEvent],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
			onSystemToggle={handleSystemToggle}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-plans-default"
				isDragging={isDragging}
				ref={draggableRef ? mergeRefs(triggerRef, draggableRef) : triggerRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<JiraEntryPointContainer
					// Data stream, will subscribe to load updates
					entryPointReferenceSubject={entryPointReferenceSubject}
					// Required props for error JSErrorBoundary.
					id="nav4-plans-default"
					packageName={PACKAGE_NAME}
					// Optional props for error JSErrorBoundary.
					errorFallback="flag"
					teamName={TEAM_NAME}
					// Fallback for Suspense
					fallback={<SkeletonNavMenuItems />}
					// Props for the root component
					runtimeProps={entryPointParams}
				/>
				<Nav4PlansFooter />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function Nav4PlansDefaultNav4({ actionsOnHover, isExpanded }: Props) {
	const cloudId = useCloudId();

	const { accountId, activationId } = useCurrentUserAndActivationId();
	const entryPointParams = useMemo(
		() => ({ cloudId, accountId, activationId }),
		[cloudId, accountId, activationId],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncPlansSectionPlanListViewQuery,
		entryPointParams,
	);

	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PLANS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();
	const menuId = MENU_ID_PLANS;

	// remove the entire useEffect when cleaning up nav4-show-plans-when-hidden-by-default
	// we should be using the route based entry point approach instead
	// https://hello.atlassian.net/wiki/spaces/UAF/pages/2754170095/Tutorial+EntryPoints+in+Routing
	// If starting life expanded, make sure to load.
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isExpanded) {
			entryPointActions.load();
		}
	}, [isExpanded, entryPointActions]);

	const onSystemToggleCallBack = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			}
		},
		[entryPointActions],
	);

	const onExpansionToggleCallBack = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.PLANS,
					sectionItem: SECTION_ITEM_PLANS,
				});
			}
		},
		[entryPointActions, sendSectionItemAnalyticsEvent],
	);

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			onSystemToggle={onSystemToggleCallBack}
			onExpansionToggle={onExpansionToggleCallBack}
			menuId={menuId}
		>
			<ExpandableMenuItemTrigger
				interactionName="nav4-sidebar-plans-default"
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				ref={triggerRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<JiraEntryPointContainer
					// Data stream, will subscribe to load updates
					entryPointReferenceSubject={entryPointReferenceSubject}
					// Required props for error JSErrorBoundary.
					id="nav4-plans-default"
					packageName={PACKAGE_NAME}
					// Optional props for error JSErrorBoundary.
					errorFallback="flag"
					teamName={TEAM_NAME}
					// Fallback for Suspense
					fallback={<SkeletonNavMenuItems />}
					// Props for the root component
					runtimeProps={entryPointParams}
				/>
				<Nav4PlansFooter />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
